import React, { useMemo } from "react";

import { useGetFarmResilienceScore, useTranslation } from "utils";

import { CarbonProgram, CarbonProgramOutcomesResponse, EVALUATION_METRICS, FieldPracticeResponse, IMPACT_PANEL_CALCULATION_TYPE, VIEW_TYPE } from "models";

import {
  Flex,
  Icon,
  PracticeAssessment,
  ResilienceScore,
  ResilienceScoreData,
  Skeleton,
  SocDevelopment,
  SocDevelopmentData,
  Tooltip,
  Typography,
} from "components";

import { ImpactPanelCollapseToggle } from "pages/app/all-field-view/components/CollapsibleImpactPanel";
import { getEvaluationMetricDatasets, normalizeDataWithBaseline } from "pages/app/field-timeline/utils/EvaluationMetricUtils";
import { buildPracticeAssessmentData } from "pages/app/field-timeline/utils/PracticeAssessmentUtils";
import { buildResilienceScoreDisplayYears, getAvailableResilienceScoreYears } from "pages/app/resilience-score/utils/ResilienceScoreContextDrawerUtils";
import { buildResilienceScoreViewModel } from "pages/app/resilience-score/utils/ResilienceScoreUtils";

const PanelNoData = ({ icon, label }: { icon: string; label: string }) => (
  <Flex align="center" justify="center" vertical gap="0.25rem" className="insights-impact-panel-no-data">
    <Icon iconName={icon} size="m" className="insights-impact-panel-no-data-icon" />
    <Typography variant="paragraph-s" className="insights-impact-panel-no-data-label">
      {label}
    </Typography>
  </Flex>
);

type InsightsImpactPanelProps = {
  reportingYear: number;
  farmId: string;
  carbonProgramOutcomes: CarbonProgramOutcomesResponse | null | undefined;
  fieldSizes: Record<string, number>;
  allFieldsPractices: Record<string, FieldPracticeResponse>;
  carbonProgram: CarbonProgram | null | undefined;
  isFetchingOutcomes?: boolean;
  isFetchingPractices?: boolean;
};

export const InsightsImpactPanel = ({
  reportingYear,
  farmId,
  carbonProgramOutcomes,
  fieldSizes,
  allFieldsPractices,
  carbonProgram,
  isFetchingOutcomes,
  isFetchingPractices,
}: InsightsImpactPanelProps) => {
  const { t } = useTranslation("fieldTimeline");
  const { data: farmResilienceApiData, isFetching: isResilienceFetching } = useGetFarmResilienceScore({ farmId });

  const resilienceScoreData = useMemo((): ResilienceScoreData | null => {
    if (!farmResilienceApiData?.length) return null;

    const availableYears = getAvailableResilienceScoreYears(farmResilienceApiData);
    if (!availableYears.length) return null;

    const displayYears = buildResilienceScoreDisplayYears(farmResilienceApiData, reportingYear, 5);

    const truncate1 = (v: number) => Math.floor(v * 10) / 10;

    const bars = displayYears.map((yr) => {
      const score = buildResilienceScoreViewModel(farmResilienceApiData, yr)?.overallScore ?? null;
      return { year: yr, value: score !== null ? truncate1(score) : null };
    });

    const barsWithValues = bars.filter((b): b is { year: number; value: number } => b.value !== null);
    if (!barsWithValues.length) return null;

    const currentScore = truncate1(buildResilienceScoreViewModel(farmResilienceApiData, reportingYear)?.overallScore ?? 0);
    const previousYear = [...availableYears].reverse().find((y) => y < reportingYear) ?? availableYears[0];
    const previousYearScore = truncate1(buildResilienceScoreViewModel(farmResilienceApiData, previousYear)?.overallScore ?? 0);
    const deltaFromBaseline = parseFloat((currentScore - previousYearScore).toFixed(1));

    return { bars, currentScore, deltaFromBaseline, baselineYear: previousYear };
  }, [farmResilienceApiData, reportingYear]);

  const socDevelopmentData = useMemo((): SocDevelopmentData | null => {
    if (!carbonProgramOutcomes) return null;

    const datasets = getEvaluationMetricDatasets(VIEW_TYPE.ALL_FIELDS, undefined, fieldSizes, carbonProgramOutcomes, IMPACT_PANEL_CALCULATION_TYPE.PER_HA, {});
    if (!datasets) return null;

    const socDataset = datasets[EVALUATION_METRICS.SOC];
    if (!socDataset?.length) return null;

    const baseline = socDataset[0]?.data.avg ?? 0;
    const normalized = normalizeDataWithBaseline(socDataset, baseline);

    const points = normalized
      .filter((item) => item.data.avg !== null)
      .map((item) => ({ year: parseInt(item.label), v: parseFloat(item.data.avg!.toFixed(2)) }));

    if (!points.length) return null;

    const currentPoint = points.filter((p) => p.year <= reportingYear).at(-1) ?? points[points.length - 1];

    return { points, currentValue: currentPoint.v, currentYear: currentPoint.year };
  }, [carbonProgramOutcomes, fieldSizes, reportingYear]);

  const practiceAssessmentData = useMemo(
    () => buildPracticeAssessmentData(allFieldsPractices, fieldSizes, reportingYear, carbonProgram, t),
    [t, allFieldsPractices, fieldSizes, reportingYear, carbonProgram]
  );

  return (
    <div className="insights-impact-panel">
      <Flex align="center" justify="space-between" className="insights-impact-panel-header">
        <Flex align="center" gap="0.75rem">
          <Typography variant="paragraph-m" bold>
            {t("impactPanel.yourImpact", "Your Impact")}
          </Typography>
          <Tooltip title={t("impactPanel.yourImpactTooltip", "Overview of your farm's environmental impact.")}>
            <span>
              <Icon iconName="tabler/info-circle" size="xs" className="insights-impact-panel-info-icon" />
            </span>
          </Tooltip>
        </Flex>
        <ImpactPanelCollapseToggle />
      </Flex>

      {isResilienceFetching ? (
        <Skeleton type="block" height="12rem" className="insights-impact-panel-skeleton" />
      ) : resilienceScoreData ? (
        <ResilienceScore data={resilienceScoreData} reportingYear={reportingYear} />
      ) : (
        <div className="resilience-score">
          <PanelNoData icon="tabler/chart-bar" label={t("impactPanel.resilienceScore.noDataAvailable", "Resilience score data not yet available.")} />
        </div>
      )}
      {isFetchingOutcomes ? (
        <Skeleton type="block" height="12rem" className="insights-impact-panel-skeleton" />
      ) : socDevelopmentData ? (
        <SocDevelopment data={socDevelopmentData} />
      ) : (
        <div className="soc-development">
          <PanelNoData icon="tabler/chart-line" label={t("impactPanel.socDevelopment.noDataAvailable", "SOC development data not yet available.")} />
        </div>
      )}
      {isFetchingPractices ? (
        <Skeleton type="block" height="12rem" className="insights-impact-panel-skeleton" />
      ) : practiceAssessmentData ? (
        <PracticeAssessment data={practiceAssessmentData} />
      ) : (
        <div className="practice-assessment">
          <PanelNoData icon="tabler/checklist" label={t("impactPanel.practiceAssessment.noDataAvailable", "Practice assessment data not yet available.")} />
        </div>
      )}
    </div>
  );
};
